import {
  Button,
  Icon,
  Image,
  Select,
  SelectOption,
  TextBody,
  TextSubtitle,
  Tooltip,
} from '@pypestream/design-system';
import { useTranslation } from '@pypestream/translations';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type {
  GroupableSolution,
  PlatformConnectionOption,
  UnconnectedChannel,
} from '../../utils';
import type { SolutionResolver } from '../../utils/resolve-solution-by-bot-id';
import { NativeClickButton } from '../solution-channel-groups/native-click-button';
import { SOLUTION_CHANNEL_GROUPS_CSS } from '../solution-channel-groups/styles';
import pypeTypeIcon from './assets/channel-type-pype.svg?url';
import voiceTypeIcon from './assets/channel-type-voice.svg?url';
import unlinkIcon from './assets/icon-unlink.svg?url';
import { UNCONNECTED_I18N_KEYS, unconnectedStatusKey } from './i18n';
import './unconnected-channels-section.scss';

export const UNCONNECTED_TEST_ID_PREFIX = 'ps-live-agent-platforms-unconnected';

export interface UnconnectedChannelsSectionProps {
  items: UnconnectedChannel[];
  /** The supported connections of this env — what the picker lists. */
  options: PlatformConnectionOption[];
  env: 'live' | 'sandbox';
  /** Viewers see the list and the status lines, no pickers or Save buttons. */
  isViewer: boolean;
  /** `null` while the solutions are unknown: the pill is then left out. */
  resolveSolution: SolutionResolver<GroupableSolution> | null;
  onGoToSolution: (solutionId: string) => void;
  /** Rejects when the save fails; resolves when the channel is connected. */
  onConnect: (item: UnconnectedChannel, connectionId: string) => Promise<void>;
  /** Called after a successful save, for the snackbar. */
  onConnected: (item: UnconnectedChannel, connectionName: string) => void;
}

/** Voice channels (Twilio / VAAPI) get the voice mark; everything else is a Pype channel. */
const isVoiceChannel = (channel: UnconnectedChannel['channel']): boolean =>
  channel.type === 'twilio_voice' || channel.type === 'vaapi';

/**
 * "Channels not connected" (PSTUDIO-4344): every channel of the env that has no
 * supported Live Agent Platform, each with a picker and an EXPLICIT per-row
 * Save. Choosing never saves. A failed save keeps the row and the user's choice
 * and says so inline.
 */
export const UnconnectedChannelsSection: FC<
  UnconnectedChannelsSectionProps
> = ({
  items,
  options,
  env,
  isViewer,
  resolveSolution,
  onGoToSolution,
  onConnect,
  onConnected,
}) => {
  const [t] = useTranslation();
  const [choice, setChoice] = useState<Record<string, string>>({});
  const [savingId, setSavingId] = useState<string | null>(null);
  const [failedIds, setFailedIds] = useState<ReadonlySet<string>>(new Set());
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  // The wrapper of each row's picker, not the `ps-select` itself: the DS element's
  // React ref type does not accept a callback ref, so focus goes through a query.
  const pickerRefs = useRef(new Map<string, HTMLElement>());
  // Where focus goes after a save settles: on success the next picker (or the
  // heading when none is left), on failure the picker of the row that failed —
  // its Save button was disabled while saving, which drops focus to the page.
  // Set when the save settles, consumed by the effect below.
  const focusAfterSaveRef = useRef<string | null | undefined>(undefined);
  const optionById = useMemo(
    () => new Map(options.map((option) => [option.id, option])),
    [options]
  );

  const save = useCallback(
    async (item: UnconnectedChannel) => {
      const { id } = item.channel;
      const connectionId = choice[id];
      if (!connectionId || savingId) return;

      setSavingId(id);
      setFailedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      try {
        await onConnect(item, connectionId);
      } catch {
        setFailedIds((prev) => new Set(prev).add(id));
        focusAfterSaveRef.current = id;
        setSavingId(null);
        return;
      }
      const index = items.findIndex((other) => other.channel.id === id);
      focusAfterSaveRef.current =
        items[index + 1]?.channel.id ?? items[index - 1]?.channel.id ?? null;
      setSavingId(null);
      onConnected(item, optionById.get(connectionId)?.name ?? '');
    },
    [choice, items, onConnect, onConnected, optionById, savingId]
  );

  useEffect(() => {
    if (focusAfterSaveRef.current === undefined) return;
    const target = focusAfterSaveRef.current;
    focusAfterSaveRef.current = undefined;
    const picker = target
      ? pickerRefs.current.get(target)?.querySelector<HTMLElement>('ps-select')
      : null;
    (picker ?? headingRef.current)?.focus();
  }, [items, savingId]);

  const envLabel = env === 'live' ? 'Live' : 'Sandbox';

  return (
    <section
      className="c-unconnected"
      aria-labelledby={`${UNCONNECTED_TEST_ID_PREFIX}-title`}
      {...{ 'test-id': `${UNCONNECTED_TEST_ID_PREFIX}-section` }}
    >
      <style>{SOLUTION_CHANNEL_GROUPS_CSS}</style>
      <div className="c-unconnected__head">
        <div className="c-unconnected__head-icon" aria-hidden="true">
          <img src={unlinkIcon} alt="" />
        </div>
        <div className="c-unconnected__head-text">
          <h2
            ref={headingRef}
            tabIndex={-1}
            id={`${UNCONNECTED_TEST_ID_PREFIX}-title`}
            className="c-unconnected__title"
            {...{ 'test-id': `${UNCONNECTED_TEST_ID_PREFIX}-title` }}
          >
            <span>{t(UNCONNECTED_I18N_KEYS.title)}</span>
            {items.length > 0 && (
              <span className="c-unconnected__count">{items.length}</span>
            )}
          </h2>
          {items.length > 0 && (
            <p className="c-unconnected__description">
              {t(UNCONNECTED_I18N_KEYS.description, { env: envLabel })}
            </p>
          )}
        </div>
      </div>
      {items.length === 0 ? (
        <div
          role="status"
          className="c-unconnected__all-connected"
          {...{ 'test-id': `${UNCONNECTED_TEST_ID_PREFIX}-all-connected` }}
        >
          <TextSubtitle size="xsmall">
            {t(UNCONNECTED_I18N_KEYS.allConnectedTitle)}
          </TextSubtitle>
          <TextBody size="small" variant="secondary">
            {t(UNCONNECTED_I18N_KEYS.allConnectedBody, { env: envLabel })}
          </TextBody>
        </div>
      ) : (
        <table className="c-unconnected__table">
          <colgroup>
            <col className="c-unconnected__col--channel" />
            <col className="c-unconnected__col--solution" />
            <col />
            {!isViewer && <col className="c-unconnected__col--connect" />}
          </colgroup>
          <thead>
            <tr>
              <th scope="col" className="c-unconnected__th">
                {t(UNCONNECTED_I18N_KEYS.colChannel)}
              </th>
              <th scope="col" className="c-unconnected__th">
                {t(UNCONNECTED_I18N_KEYS.colSolution)}
              </th>
              <th scope="col" className="c-unconnected__th">
                {t(UNCONNECTED_I18N_KEYS.colStatus)}
              </th>
              {!isViewer && (
                <th scope="col" className="c-unconnected__th">
                  {t(UNCONNECTED_I18N_KEYS.colConnectTo)}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              const { channel, status } = item;
              const solution = resolveSolution?.(channel.botId);
              const picked = choice[channel.id] ?? '';
              const isSaving = savingId === channel.id;
              const failed = failedIds.has(channel.id);
              const pickedOption = optionById.get(picked);
              const rowId = `${UNCONNECTED_TEST_ID_PREFIX}-${channel.id}`;

              return (
                <tr
                  key={channel.id}
                  className="c-unconnected__row"
                  {...{ 'test-id': rowId }}
                >
                  <td className="c-unconnected__td">
                    <div className="c-unconnected__name">
                      <img
                        className="c-unconnected__type"
                        src={
                          isVoiceChannel(channel) ? voiceTypeIcon : pypeTypeIcon
                        }
                        alt=""
                      />
                      <span className="c-unconnected__pill">
                        <span
                          className={`c-unconnected__dot c-unconnected__dot--${channel.env}`}
                          aria-hidden="true"
                        />
                        <span
                          className="c-unconnected__pill-label"
                          title={channel.name}
                        >
                          {channel.name}
                        </span>
                      </span>
                    </div>
                  </td>
                  <td className="c-unconnected__td">
                    {solution?.id && (
                      <Tooltip
                        content={solution.displayName ?? solution.name ?? ''}
                      >
                        <NativeClickButton
                          className="c-solution-group__chip"
                          test-id={`${rowId}-solution`}
                          onActivate={() =>
                            onGoToSolution(solution.id as string)
                          }
                        >
                          <span className="c-solution-group__label">
                            {solution.displayName ?? solution.name}
                          </span>
                          <Icon name="arrow-in-circle" size="xsmall" />
                        </NativeClickButton>
                      </Tooltip>
                    )}
                  </td>
                  <td className="c-unconnected__td">
                    <p className="c-unconnected__status">
                      {t(unconnectedStatusKey(status))}
                    </p>
                    <div role="alert" {...{ 'test-id': `${rowId}-error` }}>
                      {failed && (
                        <TextBody
                          size="small"
                          variant="secondary"
                          className="c-unconnected__error"
                        >
                          {t(UNCONNECTED_I18N_KEYS.saveError, {
                            channel: channel.name,
                          })}
                        </TextBody>
                      )}
                    </div>
                  </td>
                  {!isViewer && (
                    <td className="c-unconnected__td">
                      <div className="c-unconnected__picker-cell">
                        <div
                          className="c-unconnected__picker"
                          ref={(node) => {
                            if (node) pickerRefs.current.set(channel.id, node);
                            else pickerRefs.current.delete(channel.id);
                          }}
                        >
                          <Select
                            test-id={`${rowId}-picker`}
                            variant="standard"
                            size="small"
                            aria-label={String(
                              t(UNCONNECTED_I18N_KEYS.pickerLabel, {
                                channel: channel.name,
                              })
                            )}
                            placeholder={String(
                              t(UNCONNECTED_I18N_KEYS.choosePlatform)
                            )}
                            disabled={isSaving}
                            value={picked}
                            onChange={(e) =>
                              setChoice((prev) => ({
                                ...prev,
                                [channel.id]: (
                                  e.target as unknown as { value: string }
                                ).value,
                              }))
                            }
                          >
                            {pickedOption && (
                              <Image
                                src={pickedOption.logo}
                                width="24px"
                                height="24px"
                                slot="prefix"
                              />
                            )}
                            {options.map((option) => (
                              <SelectOption key={option.id} value={option.id}>
                                <Image
                                  src={option.logo}
                                  width="24px"
                                  height="24px"
                                  slot="prefix"
                                />
                                {option.name}
                              </SelectOption>
                            ))}
                          </Select>
                        </div>
                        <Button
                          test-id={`${rowId}-save`}
                          size="small"
                          disabled={!picked || isSaving}
                          onClick={() => save(item)}
                        >
                          {t(
                            isSaving
                              ? UNCONNECTED_I18N_KEYS.saving
                              : UNCONNECTED_I18N_KEYS.save
                          )}
                          <span className="c-unconnected__sr-only">
                            {' '}
                            {channel.name}
                          </span>
                        </Button>
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
};
